import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Star, Mail, Phone, Globe, Search } from "lucide-react";
import { useState } from "react";

export default function AgentMarketplace() {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: agents, isLoading } = useQuery({
    queryKey: ["verified-agents"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_registry")
        .select("*")
        .eq("verification_status", "verified")
        .order("compliance_rating", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const filteredAgents = agents?.filter((agent) => {
    const query = searchQuery.toLowerCase();
    return (
      agent.agency_name.toLowerCase().includes(query) ||
      agent.contact_name.toLowerCase().includes(query) ||
      (agent.specialties as string[])?.some((s) => s.toLowerCase().includes(query))
    );
  });

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating
            ? "fill-yellow-400 text-yellow-400"
            : "text-muted-foreground"
        }`}
      />
    ));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Agent Marketplace</h1>
          <p className="text-muted-foreground">
            Connect with verified NIL agents and representatives
          </p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by agency, name, or specialty..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2 mt-2"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-20 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredAgents?.map((agent) => (
              <Card key={agent.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle>{agent.agency_name}</CardTitle>
                  <CardDescription>{agent.contact_name}</CardDescription>
                  <div className="flex items-center gap-1 mt-2">
                    {renderStars(agent.compliance_rating || 0)}
                    <span className="text-sm text-muted-foreground ml-2">
                      ({agent.compliance_rating?.toFixed(1)})
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {agent.bio && (
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {agent.bio}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {(agent.specialties as string[])?.map((specialty) => (
                      <Badge key={specialty} variant="secondary">
                        {specialty}
                      </Badge>
                    ))}
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span>{agent.contact_email}</span>
                    </div>
                    {agent.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <span>{agent.phone}</span>
                      </div>
                    )}
                    {agent.website && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Globe className="h-4 w-4" />
                        <a
                          href={agent.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <div className="text-2xl font-bold">{agent.athlete_count}</div>
                      <div className="text-xs text-muted-foreground">Athletes</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        ${(agent.deal_volume || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground">Deal Volume</div>
                    </div>
                  </div>

                  <Button className="w-full" asChild>
                    <a href={`mailto:${agent.contact_email}`}>Connect with Agent</a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && filteredAgents?.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                No agents found matching your search criteria
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
